import React, { useState, useEffect, useRef } from "react";
import { useInView } from "../../useInView";
import { Sparkles, TrendingDown, Leaf, ShieldCheck, Award } from "lucide-react";

const StatsSection = () => {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { threshold: 0.3 });

  const stats = [
    { value: 27, label: "Global Emissions", suffix: "%", description: "from road logistics", icon: TrendingDown },
    { value: 30, label: "Carbon Reduction", suffix: "%", description: "achieved by eco-fleets", icon: Leaf },
    { value: 1000, label: "Tons of CO₂", suffix: "+", description: "saved annually", icon: ShieldCheck },
    { value: 150, label: "Business Partners", suffix: "+", description: "worldwide", icon: Award },
  ];

  return (
    <section
      ref={sectionRef}
      className="py-24 bg-gradient-to-br from-[#0E3B2E] via-[#092b21] to-[#041611] text-white relative overflow-hidden font-sans"
    >
      {/* Subtle Grid Accent Background */}
      <div className="absolute inset-0 bg-[radial-gradient(#10B981_1px,transparent_1px)] [background-size:24px_24px] opacity-10 pointer-events-none" />

      {/* Ambient Glowing Spotlights */}
      <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-emerald-500/20 rounded-full blur-3xl pointer-events-none animate-pulse" />
      <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-teal-500/20 rounded-full blur-3xl pointer-events-none" />

      <div className="max-w-6xl mx-auto px-6 relative z-10">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center gap-2 bg-emerald-500/20 border border-emerald-400/30 backdrop-blur-md px-4 py-1.5 rounded-full mb-4 shadow-sm">
            <Sparkles className="w-4 h-4 text-emerald-400" />
            <span className="text-xs sm:text-sm font-bold text-emerald-300 tracking-wide uppercase">
              Proven Environmental Impact
            </span>
          </div>

          <h2 className="text-3xl sm:text-5xl font-extrabold text-white tracking-tight">
            Our Environmental Impact
          </h2>

          <p className="text-base sm:text-lg text-emerald-100/80 mt-3 font-medium leading-relaxed">
            We're committed to making a real difference in reducing carbon emissions. Here's how we're measuring our impact on the environment.
          </p>
        </div>

        {/* 4 Stat Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
          {stats.map((stat, index) => (
            <StatCard
              key={index}
              stat={stat}
              isVisible={isInView}
              delay={index * 100}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

const StatCard = ({ stat, isVisible, delay }) => {
  const [count, setCount] = useState(0);
  const IconComp = stat.icon;

  useEffect(() => {
    let interval;

    if (isVisible) {
      const duration = 2000;
      const totalSteps = 50;
      const stepValue = stat.value / totalSteps;
      let currentStep = 0;

      interval = setInterval(() => {
        currentStep += 1;
        if (currentStep <= totalSteps) {
          setCount(Math.floor(stepValue * currentStep));
        } else {
          setCount(stat.value);
          clearInterval(interval);
        }
      }, duration / totalSteps);
    } else {
      setCount(0);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isVisible, stat.value]);

  return (
    <div
      className="bg-white/10 backdrop-blur-md border border-white/15 rounded-3xl p-8 text-center transition-all duration-500 hover:bg-white/15 hover:-translate-y-2 hover:border-emerald-400/40 shadow-xl group"
      style={{ transitionDelay: `${delay}ms` }}
    >
      <div className="w-12 h-12 rounded-2xl bg-emerald-500/20 border border-emerald-400/40 flex items-center justify-center mx-auto mb-4 text-emerald-400 group-hover:scale-110 transition-transform">
        <IconComp className="w-6 h-6" />
      </div>

      <div className="text-4xl sm:text-5xl font-black text-white tracking-tight mb-2 flex items-center justify-center gap-1">
        <span>{count.toLocaleString()}</span>
        <span className="text-emerald-400">{stat.suffix}</span>
      </div>

      <h4 className="text-sm font-extrabold text-emerald-200 uppercase tracking-wider mb-1">
        {stat.label}
      </h4>

      <p className="text-xs text-emerald-100/70 font-medium">{stat.description}</p>
    </div>
  );
};

export default StatsSection;
